import { createRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { Route as AdminLayoutRoute } from "./_layout";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { DataTable, type Column } from "@/components/application/data-table/data-table";
import { StatCard } from "@/components/application/metrics/stat-card";
import { useSyncProgressSse, type SyncStateZoneSnapshot } from "../../hooks/useSyncProgressSse";
import { useAdminAlertsSse } from "../../hooks/useAdminAlertsSse";
import type { AdminAlertEvent } from "../../schemas/anomalyResolution";
import { useBudgetList } from "../../hooks/useBudgetQueries";

export function AdminDashboardPage() {
  const { zones, snapshot: syncSnapshot } = useSyncProgressSse();
  const { snapshot: alertsSnapshot } = useAdminAlertsSse();
  const budgetsQuery = useBudgetList();

  const visibleZones = useMemo(
    () => mergeZones(zones, syncSnapshot.byZone),
    [zones, syncSnapshot.byZone],
  );
  const okZoneCount = visibleZones.filter((z) => z.last_state === "kompletni").length;
  const totalZoneCount = visibleZones.length;
  const zonesNeedAttention = totalZoneCount - okZoneCount;

  const recentAlerts = alertsSnapshot.alerts.slice(0, 5);
  const criticalCount = alertsSnapshot.alerts.filter((a) => a.severity === "critical").length;
  const openAlertsCount = alertsSnapshot.alerts.length;

  const budgetRows = budgetsQuery.data?.rows ?? [];
  const currentMonth = new Date().toISOString().slice(0, 7);
  const approvedThisMonthCount = budgetRows.filter(
    (b) => b.status === "approved" && b.month === currentMonth,
  ).length;
  const totalApproved = budgetRows.filter((b) => b.status === "approved").length;

  return (
    <section aria-labelledby="admin-dashboard-title" className="max-w-7xl mx-auto space-y-6">
      <header className="flex flex-col gap-1">
        <h2 id="admin-dashboard-title" className="text-2xl font-bold text-primary m-0">
          Dashboard
        </h2>
        <p className="text-base text-tertiary m-0">
          Souhrn provozu: stav synchronizace, otevřená upozornění, schválené rozpočty.
        </p>
      </header>

      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        data-testid="dashboard-stats"
      >
        <StatCard
          label="Synchronizace zón"
          value={`${okZoneCount} / ${totalZoneCount}`}
          helper={
            totalZoneCount === 0
              ? "Žádné zóny nejsou nakonfigurovány."
              : okZoneCount === totalZoneCount
                ? "Všechny zóny synchronizovány."
                : `${zonesNeedAttention} zóna(y) vyžaduje pozornost.`
          }
          delta={
            totalZoneCount === 0
              ? undefined
              : {
                  direction: okZoneCount === totalZoneCount ? "up" : "down",
                  label: okZoneCount === totalZoneCount ? "Vše OK" : `${zonesNeedAttention} chybí`,
                }
          }
        />
        <StatCard
          label="Otevřená upozornění"
          value={openAlertsCount}
          helper={
            criticalCount > 0
              ? `Z toho ${criticalCount} kritických.`
              : openAlertsCount === 0
                ? "Žádná nová upozornění."
                : "Žádná kritická upozornění."
          }
          delta={
            criticalCount > 0
              ? { direction: "down", label: `${criticalCount} kritická` }
              : openAlertsCount === 0
                ? { direction: "up", label: "Klid" }
                : { direction: "flat", label: "Stabilní" }
          }
        />
        <StatCard
          label="Schválené rozpočty"
          value={approvedThisMonthCount}
          helper={`Tento měsíc. Celkem schválených: ${totalApproved}.`}
          delta={
            approvedThisMonthCount > 0
              ? { direction: "up", label: "Aktivních" }
              : { direction: "flat", label: "Žádný tento měsíc" }
          }
        />
      </div>

      <Card>
        <CardHeader>
          <div>
            <h3 className="text-lg font-semibold text-primary m-0">Stav synchronizace zón</h3>
            <p className="text-sm text-tertiary m-0 mt-0.5">
              Live SSE feed — automatický refresh při novém běhu.
            </p>
          </div>
          <Link to="/admin/sync">
            <Button color="secondary" size="sm">
              Otevřít synchronizaci
            </Button>
          </Link>
        </CardHeader>
        <CardBody>
          <DataTable
            caption="Aktuální stav synchronizace per Středisko"
            columns={zoneColumns}
            rows={visibleZones}
            rowKey={(z) => z.zone_code}
            emptyMessage="Žádné zóny nejsou nakonfigurovány."
          />
        </CardBody>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <div>
              <h3 className="text-lg font-semibold text-primary m-0">Nedávná upozornění</h3>
              <p className="text-sm text-tertiary m-0 mt-0.5">
                Posledních 5 ze SSE streamu.
              </p>
            </div>
            <Link to="/admin/anomalies">
              <Button color="secondary" size="sm">
                Otevřít
              </Button>
            </Link>
          </CardHeader>
          <CardBody>
            {criticalCount > 0 && (
              <div className="mb-3">
                <InlineAlert variant="error">
                  {criticalCount === 1
                    ? "1 kritické upozornění čeká na řešení."
                    : `${criticalCount} kritická upozornění čekají na řešení.`}
                </InlineAlert>
              </div>
            )}
            {recentAlerts.length === 0 ? (
              <div className="text-center py-8 text-tertiary">Zatím žádná upozornění.</div>
            ) : (
              <ul className="list-none p-0 m-0 divide-y divide-secondary">
                {recentAlerts.map((a) => (
                  <li key={a.alert_id} className="py-3 flex items-start gap-3">
                    <BadgeWithDot color={severityBadgeColor(a.severity)} type="pill-color">
                      {severityLabel(a.severity)}
                    </BadgeWithDot>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-primary m-0">{a.title}</p>
                      <p className="text-xs text-tertiary m-0 mt-0.5">
                        {a.anomaly_type ? anomalyTypeLabel(a.anomaly_type) : "—"} ·{" "}
                        {new Date(a.timestamp).toLocaleString("cs-CZ")}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <div>
              <h3 className="text-lg font-semibold text-primary m-0">Rozpočty a předfakturace</h3>
              <p className="text-sm text-tertiary m-0 mt-0.5">
                Schválené tento měsíc + total.
              </p>
            </div>
            <Link to="/admin/budgets">
              <Button color="secondary" size="sm">
                Otevřít
              </Button>
            </Link>
          </CardHeader>
          <CardBody>
            {budgetsQuery.isLoading && <p className="text-tertiary m-0">Načítám rozpočty…</p>}
            {budgetsQuery.isError && (
              <InlineAlert variant="error">Načtení rozpočtů selhalo. Zkus obnovit stránku.</InlineAlert>
            )}
            {!budgetsQuery.isLoading && !budgetsQuery.isError && (
              <div className="space-y-4">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-primary">{approvedThisMonthCount}</span>
                  <span className="text-sm text-tertiary">schválených tento měsíc</span>
                </div>
                <div className="text-sm text-tertiary">
                  Celkem v systému: <strong className="text-primary">{totalApproved}</strong>{" "}
                  schválených.
                </div>
                <Link
                  to="/admin/budgets"
                  className="inline-block text-sm font-semibold text-brand-secondary hover:underline"
                >
                  Detail jednotlivých rozpočtů a nahrávání daňových dokladů →
                </Link>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </section>
  );
}

const zoneColumns: Column<SyncStateZoneSnapshot>[] = [
  {
    key: "code",
    header: "Zóna",
    cell: (z) => (
      <div>
        <strong className="text-primary">{z.zone_code}</strong>
        <div className="text-xs text-tertiary">{z.billing_group_code}</div>
      </div>
    ),
  },
  {
    key: "state",
    header: "Stav",
    cell: (z) => (
      <BadgeWithDot color={syncBadgeColor(z.last_state)} type="pill-color">
        {syncStateLabel(z.last_state)}
      </BadgeWithDot>
    ),
  },
  {
    key: "lastSync",
    header: "Poslední synchronizace",
    cell: (z) =>
      z.last_sync_at ? (
        new Date(z.last_sync_at).toLocaleString("cs-CZ")
      ) : (
        <span className="text-tertiary">—</span>
      ),
  },
  {
    key: "count",
    header: "Dokumentů",
    cell: (z) =>
      z.inventory_count != null ? (
        String(z.inventory_count)
      ) : (
        <span className="text-tertiary">—</span>
      ),
  },
];

function mergeZones(
  pulled: SyncStateZoneSnapshot[],
  live: Record<string, { zone_code: string; state: string; inventory_count: number; timestamp: string }>,
): SyncStateZoneSnapshot[] {
  const map = new Map<string, SyncStateZoneSnapshot>();
  pulled.forEach((z) => map.set(z.zone_code, z));
  Object.values(live).forEach((e) => {
    const existing = map.get(e.zone_code);
    if (existing) {
      map.set(e.zone_code, {
        ...existing,
        last_state: e.state,
        last_sync_at: e.timestamp,
        inventory_count: e.inventory_count,
      });
    }
  });
  return Array.from(map.values()).sort((a, b) => a.zone_code.localeCompare(b.zone_code));
}

function syncStateLabel(state: string): string {
  switch (state) {
    case "kompletni":
      return "OK";
    case "castecny":
      return "Částečná";
    case "selhal":
      return "Chyba";
    case "running":
      return "Probíhá";
    default:
      return "Bez běhu";
  }
}

function syncBadgeColor(state: string): "success" | "warning" | "error" | "blue" | "gray" {
  switch (state) {
    case "kompletni":
      return "success";
    case "castecny":
      return "warning";
    case "selhal":
      return "error";
    case "running":
      return "blue";
    default:
      return "gray";
  }
}

function severityBadgeColor(s: AdminAlertEvent["severity"]): "error" | "warning" | "blue" | "gray" {
  switch (s) {
    case "critical":
      return "error";
    case "high":
      return "warning";
    case "medium":
      return "blue";
    default:
      return "gray";
  }
}

function severityLabel(s: AdminAlertEvent["severity"]): string {
  switch (s) {
    case "critical":
      return "Kritická";
    case "high":
      return "Vysoká";
    case "medium":
      return "Střední";
    case "low":
      return "Nízká";
  }
}

function anomalyTypeLabel(t: NonNullable<AdminAlertEvent["anomaly_type"]>): string {
  switch (t) {
    case "cross_account":
      return "Cross-account";
    case "duplicate":
      return "Duplicitní platba";
    case "amount_mismatch":
      return "Neshoda částky";
    case "heuristic_proposed":
      return "Heuristické párování";
    case "unmatched":
      return "Bez kandidáta";
    default:
      return t;
  }
}

export const Route = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/admin/dashboard",
  component: AdminDashboardPage,
});
